import { mxn, fmtDate } from "@/lib/api";

// Formato tipo WhatsApp: *texto* -> negritas, \n -> salto de línea.
// Se aplica solo al renderizar (el texto se guarda tal cual, con asteriscos).
export function formatDescription(text) {
  if (!text) return "";
  let out = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  out = out.replace(/\*(.+?)\*/g, "<strong>$1</strong>");
  out = out.replace(/\n/g, "<br>");
  return out;
}

const TITLES = {
  cotizacion: "COTIZACIÓN",
  remision: "REMISIÓN",
  orden: "ORDEN DE TRABAJO",
  factura: "FACTURA",
};

export function DocumentSheet({ doc, company = {}, type, hideCosts: hideCostsProp = false, variant = "screen" }) {
  const isOrder = type === "orden";
  // Las Órdenes de Trabajo NUNCA muestran columnas de costos ni bloque de totales (producción).
  const hideCosts = hideCostsProp || isOrder;
  const title = TITLES[type] || "DOCUMENTO";
  const c = doc.client || {};
  const isQuote = type === "cotizacion";
  const isInvoice = type === "factura";
  const totalUnits = (doc.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0);
  const rate = Number(doc.iva_rate) || 0;
  const totalesFormato = doc.totales_formato || "global";
  const hideGrandTotal = isQuote && totalesFormato === "independientes";
  const remisionSinIva = type === "remision" && !doc.invoiced && !doc.requiere_factura;
  // Las Remisiones nunca muestran IVA por partida: el IVA se presenta solo al final
  // (después del subtotal, junto al total).
  const hideLineTaxCols = (isQuote && totalesFormato === "global") || remisionSinIva || type === "remision";

  const containerStyle =
    variant === "print"
      ? { width: "210mm", minHeight: "297mm", padding: "18mm" }
      : { width: "100%", maxWidth: "820px", padding: "clamp(24px, 4vw, 48px)" };
  const gradientMargin = variant === "print" ? "-mx-[18mm] -mt-[18mm]" : "-mx-[clamp(24px,4vw,48px)] -mt-[clamp(24px,4vw,48px)]";

  return (
    <div className="bg-white shadow-lg mx-auto" style={containerStyle} data-testid="document-sheet">
      <div className={`h-1.5 brand-gradient ${gradientMargin} mb-8`} />

      <div className="flex justify-between items-start border-b-2 border-[#16202B] pb-6">
        <div>
          <img src="/tas-logo.webp" alt="TAS Publicidad 360" className="w-48 mb-2" />
        </div>
        <div className="text-right">
          <div className="font-display font-bold text-2xl text-[#465B7F]">{title}</div>
          <div className="font-mono-data text-lg text-[#16202B] mt-1">{doc.number}</div>
          <div className="font-mono-data text-xs text-[#5B6672] mt-2">Fecha: {fmtDate(doc.created_at)}</div>
          {isQuote && doc.valid_until && (
            <div className="font-mono-data text-xs text-[#5B6672]">Válida hasta: {fmtDate(doc.valid_until)}</div>
          )}
          {!isQuote && doc.due_date && (
            <div className="font-mono-data text-xs text-[#5B6672]">Vence: {fmtDate(doc.due_date)}</div>
          )}
          {type === "orden" && doc.remision_number && (
            <div className="font-mono-data text-xs text-[#5B6672]">Remisión: {doc.remision_number}</div>
          )}
          {isInvoice && doc.quote_number && (
            <div className="font-mono-data text-xs text-[#5B6672]" data-testid="invoice-quote-link">Cotización: {doc.quote_number}</div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="font-mono-data text-xs uppercase tracking-widest text-[#5B6672]">Cliente</div>
        <div className="font-mono-data text-sm text-[#16202B] mt-1">
          <div className="font-semibold">{c.name}</div>
          {c.company && <div>{c.company}</div>}
          {c.rfc && <div>RFC: {c.rfc}</div>}
          {c.address && <div>{c.address}</div>}
          {c.email && <div>{c.email}</div>}
          {c.phone && <div>{c.phone}</div>}
        </div>
      </div>

      {isQuote && doc.dirigido_a && (
        <div className="mt-4" data-testid="doc-dirigido-a">
          <div className="font-mono-data text-xs uppercase tracking-widest text-[#5B6672]">Dirigido a:</div>
          <div className="font-mono-data text-sm text-[#16202B] mt-1 font-semibold">{doc.dirigido_a}</div>
        </div>
      )}

      {type === "orden" && (doc.maquina || doc.acabados || doc.material) && (
        <div className="mt-6 grid grid-cols-3 gap-4 border border-[#E3E8EE] p-4 bg-[#F7F9FB]" data-testid="ot-produccion">
          <div>
            <div className="font-mono-data text-xs uppercase tracking-widest text-[#5B6672]">Máquina a utilizar</div>
            <div className="font-mono-data text-sm text-[#16202B] mt-1 font-semibold">{doc.maquina || "—"}</div>
          </div>
          <div>
            <div className="font-mono-data text-xs uppercase tracking-widest text-[#5B6672]">Acabados</div>
            <div className="font-mono-data text-sm text-[#16202B] mt-1">{doc.acabados || "—"}</div>
          </div>
          <div>
            <div className="font-mono-data text-xs uppercase tracking-widest text-[#5B6672]">Material a utilizar</div>
            <div className="font-mono-data text-sm text-[#16202B] mt-1">{doc.material || "—"}</div>
          </div>
        </div>
      )}

      <table className="w-full mt-8 font-mono-data text-sm table-fixed" data-testid="doc-conceptos">
        <colgroup>
          {hideCosts ? (
            <>
              <col style={{ width: "70%" }} />
              <col style={{ width: "30%" }} />
            </>
          ) : hideLineTaxCols ? (
            <>
              <col style={{ width: "52%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "17%" }} />
              <col style={{ width: "17%" }} />
            </>
          ) : (
            <>
              <col style={{ width: "34%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
              <col style={{ width: "14%" }} />
            </>
          )}
        </colgroup>
        <thead>
          <tr className="border-b-2 border-[#1C1A17] text-left">
            <th className="py-2 pr-4 uppercase text-xs tracking-widest font-semibold">Concepto</th>
            <th className="py-2 px-3 uppercase text-xs tracking-widest font-semibold text-right">Cant.</th>
            {!hideCosts && <th className="py-2 px-3 uppercase text-xs tracking-widest font-semibold text-right">P. Unit.</th>}
            {!hideCosts && <th className={`py-2 uppercase text-xs tracking-widest font-semibold text-right ${hideLineTaxCols ? "pl-3" : "px-3"}`}>Importe</th>}
            {!hideCosts && !hideLineTaxCols && <th className="py-2 px-3 uppercase text-xs tracking-widest font-semibold text-right">IVA</th>}
            {!hideCosts && !hideLineTaxCols && <th className="py-2 pl-3 uppercase text-xs tracking-widest font-semibold text-right">Total</th>}
          </tr>
        </thead>
        <tbody>
          {(doc.items || []).map((it, i) => {
            const importe = (it.quantity || 0) * (it.unit_price || 0);
            const lineIva = importe * (rate / 100);
            const isTitle = Number(it.quantity) === 1 && Number(it.unit_price) === 0;
            return (
              <tr key={i} className="border-b border-[#E3E8EE] align-middle">
                <td className="py-3 pr-4 text-[#1C1A17] align-middle whitespace-normal break-words leading-snug">
                  <div dangerouslySetInnerHTML={{ __html: formatDescription(it.description) }} />
                  {isOrder && (it.maquina || it.acabados || it.material) && (
                    <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-0.5 text-xs text-[#5B6672]" data-testid={`ot-item-produccion-${i}`}>
                      {it.maquina && <span><span className="font-semibold text-[#16202B]">Máquina:</span> {it.maquina}</span>}
                      {it.acabados && <span><span className="font-semibold text-[#16202B]">Acabados:</span> {it.acabados}</span>}
                      {it.material && <span><span className="font-semibold text-[#16202B]">Material:</span> {it.material}</span>}
                    </div>
                  )}
                </td>
                <td className="py-3 px-3 text-right align-middle whitespace-nowrap">{isTitle ? "" : it.quantity}</td>
                {!hideCosts && <td className="py-3 px-3 text-right align-middle whitespace-nowrap">{isTitle ? "" : mxn(it.unit_price)}</td>}
                {!hideCosts && <td className={`py-3 text-right align-middle whitespace-nowrap text-[#1C1A17] ${hideLineTaxCols ? "pl-3" : "px-3"}`}>{isTitle ? "" : mxn(importe)}</td>}
                {!hideCosts && !hideLineTaxCols && <td className="py-3 px-3 text-right align-middle whitespace-nowrap">{isTitle ? "" : mxn(lineIva)}</td>}
                {!hideCosts && !hideLineTaxCols && <td className="py-3 pl-3 text-right align-middle whitespace-nowrap text-[#1C1A17]">{isTitle ? "" : mxn(importe + lineIva)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>

      {hideCosts ? (
        <div className="flex justify-end mt-6">
          <div className="w-64 font-mono-data text-sm">
            <div className="flex justify-between border-t-2 border-[#1C1A17] pt-2 text-base font-semibold">
              <span>TOTAL DE UNIDADES</span>
              <span className="text-[#16202B]">{totalUnits}</span>
            </div>
          </div>
        </div>
      ) : hideGrandTotal ? null : (
        <div className="flex justify-end mt-6">
          <div className="w-64 font-mono-data text-sm space-y-2">
            <Row label="Subtotal" value={mxn(doc.subtotal)} />
            {!remisionSinIva && <Row label={`IVA (${doc.iva_rate}%)`} value={mxn(doc.iva)} />}
            <div className="flex justify-between border-t-2 border-[#1C1A17] pt-2 text-lg font-semibold">
              <span>TOTAL</span>
              <span className="text-[#465B7F]">{mxn(doc.total)}</span>
            </div>
            {isInvoice && (
              <>
                <Row label="Pagado" value={mxn(doc.amount_paid)} />
                <div className="flex justify-between font-semibold">
                  <span>Saldo</span>
                  <span className="text-[#ED1F26]">{mxn(doc.balance)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {doc.notes && (
        <div className="mt-10 font-mono-data text-xs text-[#5B6672] border-t border-[#E3E8EE] pt-4">
          <div className="uppercase tracking-widest mb-1">Notas:</div>
          <div dangerouslySetInnerHTML={{ __html: formatDescription(doc.notes) }} />
        </div>
      )}

      {variant !== "print" && doc.authorized_by && (
        <div className="mt-8 font-mono-data text-xs text-[#5B6672]">
          <span className="uppercase tracking-widest">Autorizó: </span>
          <span className="text-[#16202B]">{doc.authorized_by}</span>
          {doc.authorized_at && <span> · {fmtDate(doc.authorized_at)}</span>}
        </div>
      )}

      <div className="mt-16 font-mono-data text-xs text-center text-[#5B6672] border-t border-[#E3E8EE] pt-4 space-y-1">
        <div className="text-[#16202B]">Gracias por su preferencia</div>
        <div className="text-[#16202B] font-semibold">
          {[company.name || "TAS Publicidad 360", company.rfc && `RFC: ${company.rfc}`].filter(Boolean).join("  ·  ")}
        </div>
        {company.address && <div className="whitespace-pre-line">{company.address}</div>}
        {(company.phone || company.email || company.email2) && (
          <div>{[company.phone && `Tel: ${company.phone}`, company.email, company.email2].filter(Boolean).join("  ·  ")}</div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[#5B6672]">
      <span>{label}</span>
      <span className="text-[#16202B]">{value}</span>
    </div>
  );
}
